import React, { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import AdminNavbar from "../Layout/AdminNavbar";
import TermsConditionsPopup from "../components/TermsConditionsPopup";
import { FaUsers, FaTasks, FaClock, FaChartBar } from "react-icons/fa";
import API_BASE_URL from "../config";
import { toast, ToastContainer } from "react-toastify";
import {
  PieChart, Pie, Cell, BarChart, Bar,
  XAxis, YAxis, Tooltip, CartesianGrid,
  ResponsiveContainer, Legend
} from "recharts";

/* ─── Design tokens ─────────────────────────────────────────── */
const T = {
  bg:           "#F4F6FB",
  white:        "#FFFFFF",
  border:       "#E8ECF4",
  text:         "#1A2035",
  muted:        "#8A94A6",
  indigo:       "#5B6CF9",
  emerald:      "#0EADA7",
  amber:        "#F4A12A",
  rose:         "#F45B8A",
  indigoLight:  "#EEF0FE",
  emeraldLight: "#E4F7F6",
  amberLight:   "#FEF5E4",
  roseLight:    "#FEECF3",
};

const COLORS = [T.indigo, T.emerald, T.amber, T.rose];

/* ─── Custom Tooltip ─────────────────────────────────────────── */
const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div style={{
      background: T.white,
      border: `1px solid ${T.border}`,
      borderRadius: 10,
      padding: "10px 14px",
      fontSize: 13,
      color: T.text,
      boxShadow: "0 8px 24px rgba(0,0,0,0.10)",
    }}>
      <div style={{ color: T.muted, marginBottom: 4, fontSize: 11, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.5px" }}>{label}</div>
      {payload.map((p, i) => (
        <div key={i} style={{ color: p.color || T.indigo, fontWeight: 700, fontSize: 16 }}>
          {p.name === "utilization" ? `${Number(p.value).toFixed(2)}%` : p.value}
        </div>
      ))}
    </div>
  );
};

/* ─── Two-line X-axis tick ───────────────────────────────────── */
const TwoLineTick = ({ x, y, payload }) => {
  const words = (payload.value || "").split(" ");
  const mid = Math.ceil(words.length / 2);
  return (
    <g transform={`translate(${x},${y + 6})`}>
      <text textAnchor="middle" fill={T.muted} fontSize={11} fontWeight={500}>
        <tspan x="0" dy="0">{words.slice(0, mid).join(" ")}</tspan>
        <tspan x="0" dy="14">{words.slice(mid).join(" ")}</tspan>
      </text>
    </g>
  );
};

/* ─── Stat Card ──────────────────────────────────────────────── */
const StatCard = ({ to, accent, accentLight, icon: Icon, label, value }) => {
  const [hovered, setHovered] = useState(false);
  return (
    <Link
      to={to}
      style={{
        textDecoration: "none",
        display: "flex",
        flexDirection: "column",
        gap: 18,
        background: T.white,
        border: `1.5px solid ${hovered ? accent + "55" : T.border}`,
        borderRadius: 18,
        padding: "26px 24px",
        position: "relative",
        overflow: "hidden",
        cursor: "pointer",
        transition: "transform 0.22s ease, box-shadow 0.22s ease, border-color 0.22s ease",
        transform: hovered ? "translateY(-4px)" : "translateY(0)",
        boxShadow: hovered
          ? `0 16px 40px rgba(0,0,0,0.10), 0 0 0 3px ${accent}18`
          : "0 2px 10px rgba(0,0,0,0.05)",
      }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      <div style={{
        position: "absolute", top: -24, right: -24,
        width: 90, height: 90, borderRadius: "50%",
        background: accentLight,
        opacity: 0.9,
        pointerEvents: "none",
      }} />

      <div style={{
        width: 46, height: 46, borderRadius: 13,
        background: accentLight,
        display: "flex", alignItems: "center", justifyContent: "center",
      }}>
        <Icon size={20} color={accent} />
      </div>

      <div>
        <div style={{ fontSize: 38, fontWeight: 800, color: T.text, lineHeight: 1, letterSpacing: "-1.5px" }}>
          {value}
        </div>
        <div style={{ fontSize: 13, color: T.muted, marginTop: 6, fontWeight: 500 }}>{label}</div>
      </div>

      <div style={{
        position: "absolute", bottom: 0, left: 0, right: 0,
        height: 3,
        background: `linear-gradient(90deg, ${accent}, ${accent}44)`,
        opacity: hovered ? 1 : 0,
        transition: "opacity 0.25s ease",
        borderRadius: "0 0 18px 18px",
      }} />
    </Link>
  );
};

/* ─── Chart Card ─────────────────────────────────────────────── */
const ChartCard = ({ title, subtitle, children, style }) => (
  <div style={{
    background: T.white,
    border: `1.5px solid ${T.border}`,
    borderRadius: 18,
    padding: "26px 28px",
    boxShadow: "0 2px 10px rgba(0,0,0,0.04)",
    position: "relative",
    ...style,
  }}>
    <div style={{
      display: "flex", justifyContent: "space-between",
      alignItems: "center", marginBottom: 24, flexWrap: "wrap", gap: 8,
    }}>
      <div style={{ fontSize: 16, fontWeight: 700, color: T.text, letterSpacing: "-0.2px" }}>{title}</div>
      {subtitle && (
        <span style={{
          fontSize: 11, fontWeight: 600, color: T.muted,
          background: T.bg, borderRadius: 999, padding: "4px 12px",
          border: `1px solid ${T.border}`, letterSpacing: "0.3px",
        }}>
          {subtitle}
        </span>
      )}
    </div>
    {children}
  </div>
);

/* ─── Dashboard ──────────────────────────────────────────────── */
const Dashboard = () => {
  const navigate = useNavigate();
  const [username, setUsername] = useState("Admin");
  const [totalEmployees, setTotalEmployees] = useState(0);
  const [activeTasks, setActiveTasks] = useState(0);
  const [pendingLeaves, setPendingLeaves] = useState(0);
  const [timesheetCount, setTimesheetCount] = useState(0);
  const [financialYear, setFinancialYear] = useState("");
  const [chartData, setChartData] = useState([]);
  const [utilizationData, setUtilizationData] = useState([]);
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setTimeout(() => setMounted(true), 60);
    const token = localStorage.getItem("token");
    const roles = JSON.parse(localStorage.getItem("roles") || "[]");
    if (!token) { toast.error("You must be logged in."); navigate("/"); return; }
    if (!roles.includes("ROLE_ADMIN")) { toast.error("Unauthorized."); navigate("/"); return; }
  }, [navigate]);

  useEffect(() => {
    const token = localStorage.getItem("token");
    if (!token) return;
    const now = new Date();
    const month = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;
    fetch(`${API_BASE_URL}/api/allocation?month=${month}`, { headers: { Authorization: "Bearer " + token } })
      .then(r => r.json())
      .then(data => {
        if (!Array.isArray(data)) return;
        setUtilizationData(data.map(e => ({ name: e.employeeName, utilization: e.overallUtilization })));
      }).catch(console.error);
  }, []);

  useEffect(() => {
    const token = localStorage.getItem("token");
    fetch(`${API_BASE_URL}/api/company-info/financial-year`, { headers: { Authorization: "Bearer " + token } })
      .then(r => r.json())
      .then(data => {
        if (data?.financialYear) setFinancialYear(data.financialYear);
      }).catch(console.error);
  }, []);

  useEffect(() => {
  const token = localStorage.getItem("token");

  fetch(`${API_BASE_URL}/api/employees/active/count`, {
    headers: {
      Authorization: `Bearer ${token}`
    }
  })
    .then(res => res.json())
    .then(data => setTotalEmployees(data.activeEmployeeCount))
    .catch(console.error);

}, []);

  useEffect(() => {
    const token = localStorage.getItem("token");
    if (!token) return;
    fetch(`${API_BASE_URL}/api/tasks/in-progress-count`, { headers: { Authorization: "Bearer " + token } })
      .then(r => r.json())
      .then(data => setActiveTasks(Number(data?.inProgressCount) || 0))
      .catch(console.error);
  }, []);

  useEffect(() => {
    if (!financialYear) return;
    const token = localStorage.getItem("token");
    fetch(`${API_BASE_URL}/api/leave-transactions/pending?financialYear=${financialYear}`, { headers: { Authorization: "Bearer " + token } })
      .then(r => r.json())
      .then(data => {
        const leaves = Array.isArray(data) ? data : [];
        setPendingLeaves(leaves.filter(l => l.leaveStatus === "PENDING" || l.leaveStatus === "FORWARDED").length);
      }).catch(console.error);
  }, [financialYear]);

  useEffect(() => {
    const token = localStorage.getItem("token");
    fetch(`${API_BASE_URL}/api/dashboard`, { headers: { Authorization: "Bearer " + token } })
      .then(r => r.json()).then(d => { if (d.username) setUsername(d.username); }).catch(console.error);
  }, []);

  useEffect(() => {
    const token = localStorage.getItem("token");
    if (!token) return;
    fetch(`${API_BASE_URL}/api/worklogs/active-timesheet-count`, { headers: { Authorization: "Bearer " + token } })
      .then(r => r.json())
      .then(data => setTimesheetCount(Number(data?.activeTimesheetCount) || 0))
      .catch(console.error);
  }, []);

  useEffect(() => {
    setChartData([
      { name: "Employees",      value: totalEmployees },
      { name: "Active Tasks",   value: activeTasks    },
      { name: "Pending Leaves", value: pendingLeaves  },
    ]);
  }, [totalEmployees, activeTasks, pendingLeaves]);

  const today = new Date().toLocaleDateString("en-US", {
    weekday: "long", month: "long", day: "numeric", year: "numeric",
  });

  return (
    <>
      <TermsConditionsPopup />
      <ToastContainer />

      <link
        href="https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700;800&display=swap"
        rel="stylesheet"
      />

      <style>{`
        * { box-sizing: border-box; }

        @keyframes fadeUp {
          from { opacity: 0; transform: translateY(16px); }
          to   { opacity: 1; transform: translateY(0);    }
        }
        .fu { animation: fadeUp 0.45s ease both; }
        .d1 { animation-delay: 0.06s; }
        .d2 { animation-delay: 0.12s; }
        .d3 { animation-delay: 0.20s; }
        .d4 { animation-delay: 0.28s; }
        .d5 { animation-delay: 0.36s; }

        .recharts-cartesian-axis-tick text { fill: ${T.muted} !important; font-size: 12px; }
        .recharts-legend-item-text         { color: ${T.muted} !important; font-size: 12px !important; }
        .recharts-cartesian-grid line      { stroke: ${T.border} !important; }

        /* ── Scoped scrollbar ── */
        .admin-scroll::-webkit-scrollbar { width: 6px; }
        .admin-scroll::-webkit-scrollbar-track { background: transparent; }
        .admin-scroll::-webkit-scrollbar-thumb { background: #c5cfe0; border-radius: 99px; }
        .admin-scroll::-webkit-scrollbar-thumb:hover { background: #a0b0c8; }

        @media (max-width: 1100px) {
          .g4 { grid-template-columns: repeat(2,1fr) !important; }
          .g2 { grid-template-columns: 1fr !important; }
        }
        @media (max-width: 600px) {
          .g4 { grid-template-columns: 1fr !important; }
        }
      `}</style>

      {/* ── Fixed Navbar ── */}
      <div className="fixed-top"><AdminNavbar /></div>

      {/* ── Full-screen canvas ── */}
      <div style={{
        width: "100%",
        height: "100vh",
        display: "flex",
        flexDirection: "column",
        background: T.bg,
        fontFamily: "'Plus Jakarta Sans', sans-serif",
        color: T.text,
        overflow: "hidden",
      }}>
        {/* ── Scrollable area ── */}
        <div
          className="admin-scroll"
          style={{
            flex: 1,
            overflowY: "auto",
            overflowX: "hidden",
          }}
        >
          {/* ── Content wrapper ── */}
          <div style={{ width: "100%", padding: "120px 32px 60px" }}>

            {/* ── Header ── */}
            <div className={mounted ? "fu" : ""} style={{
              display: "flex", justifyContent: "space-between",
              alignItems: "flex-start", flexWrap: "wrap", gap: 16, marginBottom: 32,
            }}>
              <div>
                <h1 style={{ fontSize: 28, fontWeight: 800, color: T.text, margin: 0, letterSpacing: "-0.5px" }}>
                  👋 Welcome back,{" "}
                  <span style={{ color: T.indigo }}>{username}</span>
                </h1>
                <p style={{ color: T.muted, fontSize: 14, margin: "6px 0 0", fontWeight: 500 }}>{today}</p>
              </div>

              {/* <div style={{
                display: "flex", alignItems: "center", gap: 8,
                background: T.white, border: `1.5px solid ${T.border}`,
                borderRadius: 12, padding: "10px 18px",
                fontSize: 13, color: T.muted, fontWeight: 600,
                boxShadow: "0 2px 8px rgba(0,0,0,0.04)",
              }}>
                <span style={{
                  width: 8, height: 8, borderRadius: "50%",
                  background: T.emerald, display: "inline-block",
                  boxShadow: `0 0 0 3px ${T.emeraldLight}`,
                }} />
                All Systems Operational
              </div> */}
            </div>

            {/* ── Stat Cards ── */}
            <div className={`g4 ${mounted ? "fu d1" : ""}`} style={{
              display: "grid",
              gridTemplateColumns: "repeat(4, 1fr)",
              gap: 18,
              marginBottom: 22,
            }}>
              <StatCard to="/allemployee"      accent={T.indigo}  accentLight={T.indigoLight}  icon={FaUsers}    label="Total Employees"          value={totalEmployees} />
              <StatCard to="/adminviewtasks"   accent={T.emerald} accentLight={T.emeraldLight} icon={FaTasks}    label="Ongoing Tasks"            value={activeTasks} />
              <StatCard to="/pendingleavelist" accent={T.amber}   accentLight={T.amberLight}   icon={FaClock}    label="Leaves Awaiting Approval" value={pendingLeaves} />
              <StatCard to="/alltimesheetgrid" accent={T.rose}    accentLight={T.roseLight}    icon={FaChartBar} label="Active Timesheets"        value={timesheetCount} />
            </div>

            {/* ── Overview + Donut ── */}
            <div className={`g2 ${mounted ? "fu d3" : ""}`} style={{
              display: "grid",
              gridTemplateColumns: "2fr 1fr",
              gap: 18,
              marginBottom: 22,
            }}>
              <ChartCard title="Overview Summary" subtitle="">
                <ResponsiveContainer width="100%" height={290}>
                  <BarChart data={chartData} barCategoryGap="45%">
                    <CartesianGrid strokeDasharray="4 4" vertical={false} />
                    <XAxis dataKey="name" axisLine={false} tickLine={false} tick={{ fontSize: 13, fill: T.muted, fontWeight: 600 }} />
                    <YAxis axisLine={false} tickLine={false} tick={{ fontSize: 12, fill: T.muted }} />
                    <Tooltip content={<CustomTooltip />} cursor={{ fill: `${T.indigo}08`, radius: 6 }} />
                    <Bar dataKey="value" radius={[8, 8, 0, 0]}>
                      {chartData.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </ChartCard>

              <ChartCard title="Distribution" subtitle="" style={{ position: "relative" }}>
                <ResponsiveContainer width="100%" height={290}>
                  <PieChart>
                    <Pie
                      data={chartData}
                      dataKey="value"
                      nameKey="name"
                      innerRadius={68}
                      outerRadius={108}
                      paddingAngle={3}
                      strokeWidth={0}
                    >
                      {chartData.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                    </Pie>
                    <Tooltip content={<CustomTooltip />} />
                    <Legend
                      iconType="circle" iconSize={8}
                      formatter={v => <span style={{ color: T.muted, fontSize: 12, fontWeight: 500 }}>{v}</span>}
                    />
                  </PieChart>
                </ResponsiveContainer>
                {/* Center label */}
                <div style={{
                  position: "absolute", top: "48%", left: "50%",
                  transform: "translate(-50%, -68%)",
                  textAlign: "center", pointerEvents: "none",
                }}>
                  <div style={{ fontSize: 24, fontWeight: 800, color: T.text }}>{totalEmployees + activeTasks + pendingLeaves}</div>
                  <div style={{ fontSize: 10, color: T.muted, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.5px" }}>Total</div>
                </div>
              </ChartCard>
            </div>

            {/* ── Utilization ── */}
            <div className={mounted ? "fu d5" : ""}>
              <ChartCard title="Employee Utilization" subtitle="">
                <div style={{ overflowX: "auto" }}>
                  <div style={{ width: Math.max(utilizationData.length * 90, 900), height: 380 }}>
                    <ResponsiveContainer>
                      <BarChart data={utilizationData} margin={{ top: 10, right: 16, left: 0, bottom: 30 }} barCategoryGap="35%">
                        <CartesianGrid strokeDasharray="4 4" vertical={false} />
                        <XAxis dataKey="name" interval={0} height={80} tick={<TwoLineTick />} axisLine={false} tickLine={false} />
                        <YAxis domain={[0, 100]} axisLine={false} tickLine={false} tick={{ fontSize: 12, fill: T.muted }} tickFormatter={v => `${v}%`} />
                        <Tooltip content={<CustomTooltip />} cursor={{ fill: `${T.indigo}06`, radius: 6 }} />
                        <Bar dataKey="utilization" radius={[8, 8, 0, 0]} barSize={46}>
                          {utilizationData.map((entry, i) => {
                            const color = entry.utilization >= 80 ? T.emerald : entry.utilization >= 50 ? T.indigo : T.amber;
                            return <Cell key={i} fill={color} />;
                          })}
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </div>

                {/* Legend pills */}
                <div style={{ display: "flex", gap: 10, marginTop: 18, flexWrap: "wrap" }}>
                  {[
                    { color: T.emerald, light: T.emeraldLight, label: "High ≥ 80%"    },
                    { color: T.indigo,  light: T.indigoLight,  label: "Medium 50–79%" },
                    { color: T.amber,   light: T.amberLight,   label: "Low < 50%"     },
                  ].map(({ color, light, label }) => (
                    <span key={label} style={{
                      display: "inline-flex", alignItems: "center", gap: 7,
                      padding: "5px 14px", borderRadius: 999,
                      background: light, color,
                      fontSize: 12, fontWeight: 700, letterSpacing: "0.2px",
                    }}>
                      <span style={{ width: 7, height: 7, borderRadius: "50%", background: color, display: "inline-block" }} />
                      {label}
                    </span>
                  ))}
                </div>
              </ChartCard>
            </div>

          </div>{/* end content wrapper */}
        </div>{/* end admin-scroll */}
      </div>{/* end full-screen canvas */}
    </>
  );
};

export default Dashboard;
